import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Camera, ExternalLink, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import ProPortalHeader from '@/components/ProPortalHeader';
import { FILE_CATEGORY_LABELS, wget, wpost } from '@/components/website/api';
import { FileUploader } from '@/components/website/FileUploader';

const HINTS = { projects: 'Your best finished work — these fill the gallery on your website.', before_after: 'Pairs of before/after shots work brilliantly.', team: 'You, your team, your workshop.', vehicles: 'Sign-written vans, workwear, banners.', other: 'Anything else you want on the site.' };

export default function MyWebsitePhotos() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  useEffect(() => { wget(`/website/orders/${id}`).then(setOrder).catch((e) => toast.error(e.message)); }, [id]);
  if (!order) return <div className="min-h-screen bg-[#E8ECEF]"><ProPortalHeader /><Loader2 className="w-6 h-6 animate-spin text-slate-400 mx-auto mt-16" /></div>;

  const photos = (order.files || []).filter((f) => ['projects', 'before_after', 'team', 'vehicles', 'other'].includes(f.category));
  const canPublish = !!order.rollout?.started_at;
  const publish = async () => {
    setBusy(true);
    try { const d = await wpost(`/website/orders/${id}/republish`, {}); toast.success(d.detail || 'Your website is being updated with the new photos'); setDirty(false); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen bg-[#E8ECEF]" data-testid="my-photos-page">
      <ProPortalHeader />
      <main className="max-w-3xl mx-auto px-4 py-6 md:py-10 space-y-5">
        <button onClick={() => navigate(`/my-website/${id}`)} className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900" data-testid="mwp-back"><ArrowLeft className="w-4 h-4" /> Back to my website</button>
        <div>
          <h1 className="text-3xl font-bold text-[#27292E] flex items-center gap-2"><Camera className="w-7 h-7 text-teal-700" /> My Photos</h1>
          <p className="text-slate-600 mt-1">Add photos any time — straight from your phone. <b>{photos.length}</b> photo{photos.length === 1 ? '' : 's'} on file{order.form?.business?.business_name ? ` for ${order.form.business.business_name}` : ''}.</p>
        </div>
        {canPublish && (
          <div className={`rounded-2xl border p-4 flex flex-wrap items-center justify-between gap-3 ${dirty ? 'bg-amber-50 border-amber-300' : 'bg-white border-slate-200'}`} data-testid="mwp-publish-box">
            <div className="text-sm">
              <div className="font-bold text-slate-900">{dirty ? 'You have new photos — update your website to show them' : 'Changed your photos? Push them to your website'}</div>
              <div className="text-xs text-slate-500">Rebuilds your site with the current photos{order.status === 'live' ? ' and updates the live site within a few minutes' : ''}. You can do this once every 10 minutes.</div>
            </div>
            <Button onClick={publish} disabled={busy} className="h-10 bg-teal-600 hover:bg-teal-500 text-white font-bold" data-testid="mwp-publish">{busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />} Update my website</Button>
          </div>
        )}
        {order.admin?.preview_url && <a href={order.admin.live_url || order.admin.preview_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm font-semibold text-teal-700 underline" data-testid="mwp-view-site"><ExternalLink className="w-4 h-4" /> View my website</a>}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-6">
          {['projects', 'before_after', 'team', 'vehicles', 'other'].map((c) => (
            <FileUploader key={c} order={order} category={c} label={FILE_CATEGORY_LABELS[c]} hint={HINTS[c]} optional onChange={(upd) => { setOrder((cur) => (typeof upd === 'function' ? upd(cur) : upd)); setDirty(true); }} />
          ))}
        </div>
      </main>
    </div>
  );
}
